import React,{useState,useEffect,useCallback}from'react';
import{useNavigate,useSearchParams}from'react-router-dom';
import Layout from'../components/layout/Layout';
import api from'../services/api';
import PatientSearchInput from'../components/patient/PatientSearchInput';
import {getCurrencySymbol} from '../utils/locale';
import {useTranslation} from 'react-i18next';
import FileShaClaimButton from '../components/common/FileShaClaimButton';

const DEFAULT_SLOTS=[
  '08:00','08:30','09:00','09:30','10:00','10:30',
  '11:00','11:30','12:00','12:30','14:00','14:30',
  '15:00','15:30','16:00','16:30','17:00',
];

const FLD=({label,required,children}:any)=>(
  <div><label className="block text-sm font-medium text-gray-700 mb-1">{label}{required&&<span className="text-red-500 ml-1">*</span>}</label>{children}</div>
);
const SEL=({value,onChange,children}:any)=>(
  <select value={value} onChange={onChange} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#015CA6] outline-none">{children}</select>
);
const INP=({value,onChange,type='text',placeholder='',min=''}:any)=>(
  <input type={type} value={value} onChange={onChange} placeholder={placeholder} min={min}
    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#015CA6] outline-none"/>
);

const SlotCard=({time,selected,booked=0,capacity=1,overbookingLimit=0,fromRoster=false,isPast=false,onSelect}:any)=>{
  const isFull=booked>=(capacity+overbookingLimit);
  const disabled=isFull||isPast;
  const isNearFull=!isFull&&fromRoster&&booked>=(capacity*0.8);
  const isOverbooked=booked>capacity;
  const pct=capacity>0?Math.min(100,Math.round(booked/capacity*100)):0;
  return(
    <button type="button" disabled={disabled} onClick={onSelect}
      className={`relative flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all
        ${disabled?'opacity-40 cursor-not-allowed border-slate-200 bg-slate-50':
          selected?'border-[#02357A] bg-blue-50 shadow-md ring-2 ring-[#02357A]/20':
          isNearFull?'border-amber-300 bg-amber-50 hover:border-amber-400':
          'border-slate-200 bg-white hover:border-[#02357A] hover:shadow-sm'}`}>
      <span className={`text-sm font-bold ${selected?'text-[#02357A]':disabled?'text-slate-400':'text-slate-700'}`}>{time}</span>
      {isPast?(
        <span className="text-xs text-slate-400 mt-1">Past</span>
      ):fromRoster?(
        <div className="w-full mt-1.5">
          <div className="w-full bg-slate-200 rounded-full h-1 mb-1">
            <div className={`h-1 rounded-full ${isFull?'bg-red-500':isOverbooked?'bg-orange-500':isNearFull?'bg-amber-500':'bg-green-500'}`} style={{width:`${pct}%`}}/>
          </div>
          <span className={`text-xs ${isFull?'text-red-600':isOverbooked?'text-orange-600':isNearFull?'text-amber-600':'text-green-600'}`}>
            {isFull?'FULL':`${booked}/${capacity}`}
          </span>
        </div>
      ):(
        <span className="text-xs text-green-600 mt-1">Open</span>
      )}
      {selected&&!disabled&&(
        <div className="absolute -top-2 -right-2 w-5 h-5 bg-[#02357A] rounded-full flex items-center justify-center shadow">
          <span className="text-white text-xs font-bold">✓</span>
        </div>
      )}
    </button>
  );
};

export default function AppointmentBookingPage(){
  const {t}=useTranslation();
  const nav=useNavigate();
  const [sp]=useSearchParams();
  const [loading,setLoading]=useState(true);
  const [doctors,setDoctors]=useState<any[]>([]);
  const [departments,setDepartments]=useState<any[]>([]);
  const [slots,setSlots]=useState<any[]>([]);
  const [slotsLoading,setSlotsLoading]=useState(false);
  const [slotsMsg,setSlotsMsg]=useState('');
  const [patient,setPatient]=useState<any>(null);
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');
  const [error,setError]=useState('');
  const [lastBooked,setLastBooked]=useState<any>(null);
  const [specialtyFilter,setSpecialtyFilter]=useState('');

  const [form,setForm]=useState({
    patientId:sp.get('patientId')||'',
    doctorId:sp.get('doctorId')||'',
    departmentId:'',
    appointmentDate:new Date().toISOString().split('T')[0],
    appointmentTime:'',
    consultationType:'OPD',
    reasonForVisit:'',
    chiefComplaint:'',
    notes:'',
    priority:'ROUTINE',
    referralSource:'SELF',
    referredBy:'',
    isFollowUp:false,
    paymentMode:'CASH',
    consultationFee:'',
    reminderSms:true,
    reminderWhatsapp:false,
    fastingRequired:false,
    specialInstructions:'',
  });
  const f=(k:string,v:any)=>setForm(x=>({...x,[k]:v}));

  // Past time check — only applies if selected date is today
  const isTimeSlotPast=useCallback((slotTime:string)=>{
    const today=new Date().toISOString().split('T')[0];
    if(form.appointmentDate!==today)return false;
    const [h,m]=slotTime.split(':').map(Number);
    const now=new Date();
    return now.getHours()>h||(now.getHours()===h&&now.getMinutes()>=m);
  },[form.appointmentDate]);

  const specialties=Array.from(new Set(doctors.map((d:any)=>d.specialization||d.speciality).filter(Boolean))).sort() as string[];
  const filteredDoctors=specialtyFilter?doctors.filter((d:any)=>(d.specialization||d.speciality)===specialtyFilter):doctors;

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const[d,dep]=await Promise.all([
        api.getDoctors?.().catch(()=>({data:[]})),
        api.getDepartments?.().catch(()=>({data:[]})),
      ]);
      setDoctors(Array.isArray(d?.data)?d.data:d?.data?.doctors||d?.data?.data||[]);
      setDepartments(Array.isArray(dep?.data)?dep.data:dep?.data?.data||[]);
    }catch{}
    if(sp.get('patientId')){
      api.getPatient?.(sp.get('patientId')!).then((r:any)=>r?.data&&setPatient(r.data)).catch(()=>{});
    }
    setLoading(false);
  },[sp]);

  useEffect(()=>{load();},[load]);

  // Load slots when doctor + date changes
  useEffect(()=>{
    if(!form.doctorId||!form.appointmentDate){setSlots([]);setSlotsMsg('');return;}
    setSlotsLoading(true);setSlots([]);f('appointmentTime','');
    api.getRosterAvailableSlots?.(form.doctorId,form.appointmentDate)
      .then((r:any)=>{
        const list=r?.data||[];
        if(list.length>0){
          setSlots(list.map((s:any)=>({
            time:(s.slot_time||s.slotTime||s.time||'').slice(0,5),
            booked:+(s.booked||0),capacity:+(s.capacity||1),
            overbookingLimit:+(s.overbooking_limit||s.overbookingLimit||0),
            fromRoster:true,
          })));
          setSlotsMsg('');
        }else{
          return api.getDoctorSlots?.(form.doctorId,form.appointmentDate).then((r2:any)=>{
            const list2=Array.isArray(r2?.data)?r2.data:r2?.data?.slots||r2?.data?.data||[];
            if(list2.length>0){
              setSlots(list2.map((s:any)=>({time:(s.start_time||s.slotTime||s.time||String(s)).slice(0,5),booked:0,capacity:1,overbookingLimit:0,fromRoster:false})));
              setSlotsMsg('');
            }else{
              setSlots([]);setSlotsMsg('No roster configured — showing default slots');
            }
          }).catch(()=>{setSlots([]);setSlotsMsg('No roster configured — showing default slots');});
        }
      })
      .catch(()=>{setSlots([]);setSlotsMsg('No roster configured — showing default slots');})
      .finally(()=>setSlotsLoading(false));
  },[form.doctorId,form.appointmentDate]);

  // Auto-fill consultation fee from doctor
  useEffect(()=>{
    if(!form.doctorId)return;
    const doc=doctors.find((d:any)=>d.id===form.doctorId);
    if(doc){
      const fee=doc.consultationFee||doc.consultation_fee||'';
      if(fee)f('consultationFee',String(fee));
    }
  },[form.doctorId,doctors]);

  // Clear selected time if it becomes past (date changed to today)
  useEffect(()=>{
    if(form.appointmentTime&&isTimeSlotPast(form.appointmentTime)){
      f('appointmentTime','');
    }
  },[form.appointmentDate,form.appointmentTime,isTimeSlotPast]);

  const submit=async()=>{
    if(!form.patientId){setError('Please select a patient');return;}
    if(!form.doctorId){setError('Please select a doctor');return;}
    if(!form.appointmentDate){setError('Please select a date');return;}
    if(!form.appointmentTime){setError('Please select a time slot');return;}
    if(isTimeSlotPast(form.appointmentTime)){setError('Selected time has already passed. Please choose a future slot.');return;}
    setSaving(true);setError('');
    try{
      const r=await api.createAppointment?.({
        patientId:form.patientId,doctorId:form.doctorId,
        departmentId:form.departmentId||null,
        appointmentDate:form.appointmentDate,
        appointmentTime:form.appointmentTime,
        consultationType:form.consultationType,
        reasonForVisit:form.reasonForVisit||null,
        chiefComplaint:form.chiefComplaint||null,
        notes:form.notes||null,priority:form.priority,
        referralSource:form.referralSource||null,
        referredBy:form.referredBy||null,
        isFollowUp:form.isFollowUp,
        paymentMode:form.paymentMode,
        consultationFee:form.consultationFee?parseFloat(form.consultationFee):null,
        reminderSms:form.reminderSms,
        reminderWhatsapp:form.reminderWhatsapp,
        fastingRequired:form.fastingRequired,
        specialInstructions:form.specialInstructions||null,
      });
      setLastBooked(r?.data?.data||r?.data||{id:form.patientId,patientId:form.patientId});
      setMsg('✅ Appointment booked. File SHA if this is a SHIF visit, or go to the queue.');
    }catch(e:any){setError('❌ '+(e.response?.data?.message||'Booking failed. Please try again.'));}
    setSaving(false);
  };

  // Slot selector helper
  const renderSlots=(slotList:any[],fromRoster:boolean)=>(
    <div className="grid grid-cols-5 gap-2">
      {slotList.map((s:any)=>{
        const time=typeof s==='string'?s:s.time;
        const past=isTimeSlotPast(time);
        return(
          <SlotCard key={time}
            time={time}
            booked={s.booked||0}
            capacity={s.capacity||1}
            overbookingLimit={s.overbookingLimit||0}
            fromRoster={fromRoster}
            isPast={past}
            selected={form.appointmentTime===time}
            onSelect={()=>{if(!past)f('appointmentTime',form.appointmentTime===time?'':time);}}
          />
        );
      })}
    </div>
  );

  return(
    <Layout>
      <div className="p-6 max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Book Appointment</h1>
            <p className="text-gray-500 text-sm mt-1">Schedule a new patient appointment</p>
          </div>
          <button onClick={()=>nav('/appointments')} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50 text-gray-600">← Back</button>
        </div>

        {error&&<div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
        {msg&&<div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm font-semibold flex flex-wrap items-center justify-between gap-3">
          <span>{msg}</span>
          {lastBooked&&(
            <div className="flex gap-2 items-center">
              <FileShaClaimButton
                patientId={form.patientId}
                patientName={patient?`${patient.firstName||patient.first_name||''} ${patient.lastName||patient.last_name||''}`.trim():''}
                patientCrId={patient?.shaBeneficiaryCrId||patient?.sha_beneficiary_cr_id}
                encounterId={lastBooked.id||form.patientId}
                serviceName={form.reasonForVisit||form.consultationType||'OPD appointment'}
                from="appointment"
              />
              <button onClick={()=>nav('/appointments')} className="text-xs font-bold px-3 py-1.5 rounded-lg bg-[#02357A] text-white">Queue →</button>
            </div>
          )}
        </div>}

        <div className="space-y-4">

          {/* Patient */}
          <div className="bg-white rounded-xl shadow-sm border p-5">
            <h3 className="font-bold text-gray-800 mb-4 pb-2 border-b">{t('common.patient','Patient')}</h3>
            <PatientSearchInput
              label="Search Patient *"
              selected={patient}
              onSelect={(p:any)=>{setPatient(p);f('patientId',p.id);}}
              onClear={()=>{setPatient(null);f('patientId','');}}
              placeholder="Search by name, UHID or phone..."
            />
          </div>

          {/* Doctor & Schedule */}
          <div className="bg-white rounded-xl shadow-sm border p-5">
            <h3 className="font-bold text-gray-800 mb-4 pb-2 border-b">Doctor & Schedule</h3>
            <div className="grid grid-cols-2 gap-4">
              <FLD label="Filter by Specialty">
                <SEL value={specialtyFilter} onChange={(e:any)=>{setSpecialtyFilter(e.target.value);f('doctorId','');}}>
                  <option value="">All Specialties</option>
                  {specialties.map(s=><option key={s} value={s}>{s}</option>)}
                </SEL>
              </FLD>
              <FLD label="Doctor" required>
                <SEL value={form.doctorId} onChange={(e:any)=>f('doctorId',e.target.value)}>
                  <option value="">Select Doctor</option>
                  {filteredDoctors.map((d:any)=>(
                    <option key={d.id} value={d.id}>
                      Dr. {d.firstName||d.first_name} {d.lastName||d.last_name}
                      {(d.specialization||d.speciality)?` — ${d.specialization||d.speciality}`:''}
                    </option>
                  ))}
                </SEL>
              </FLD>
              <FLD label="Appointment Date" required>
                <INP type="date" value={form.appointmentDate} onChange={(e:any)=>f('appointmentDate',e.target.value)} min={new Date().toISOString().split('T')[0]}/>
              </FLD>
              <FLD label="Department">
                <SEL value={form.departmentId} onChange={(e:any)=>f('departmentId',e.target.value)}>
                  <option value="">Select Department</option>
                  {departments.map((d:any)=><option key={d.id} value={d.id}>{d.department_name||d.departmentName||d.name}</option>)}
                </SEL>
              </FLD>
            </div>

            {/* TIME SLOTS */}
            {(form.doctorId&&form.appointmentDate)&&(
              <div className="mt-5">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-700">
                    Select Time Slot <span className="text-red-500">*</span>
                  </label>
                  {form.appointmentTime&&(
                    <span className="text-sm font-bold text-[#02357A] bg-blue-50 px-3 py-1 rounded-lg border border-blue-200">
                      ⏰ {form.appointmentTime} selected
                    </span>
                  )}
                </div>

                {slotsLoading?(
                  <div className="grid grid-cols-5 gap-2">
                    {[...Array(10)].map((_,i)=>(
                      <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse"/>
                    ))}
                  </div>
                ):slots.length>0?(
                  <>
                    {slotsMsg&&<p className="text-xs text-amber-600 mb-2">⚠️ {slotsMsg}</p>}
                    {renderSlots(slots,true)}
                  </>
                ):(
                  <>
                    {slotsMsg&&<p className="text-xs text-slate-500 mb-3">ℹ️ {slotsMsg}</p>}
                    {renderSlots(DEFAULT_SLOTS,false)}
                    <p className="text-xs text-slate-400 mt-2">* Grey slots are in the past and cannot be selected</p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Appointment Details */}
          <div className="bg-white rounded-xl shadow-sm border p-5">
            <h3 className="font-bold text-gray-800 mb-4 pb-2 border-b">Appointment Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <FLD label="Consultation Type">
                <SEL value={form.consultationType} onChange={(e:any)=>f('consultationType',e.target.value)}>
                  {['OPD','FOLLOW_UP','TELECONSULT','EMERGENCY','REVIEW','SECOND_OPINION'].map(t=><option key={t}>{t}</option>)}
                </SEL>
              </FLD>
              <FLD label="Priority">
                <SEL value={form.priority} onChange={(e:any)=>f('priority',e.target.value)}>
                  {['ROUTINE','URGENT','EMERGENCY'].map(p=><option key={p}>{p}</option>)}
                </SEL>
              </FLD>
              <div className="col-span-2">
                <FLD label="Reason for Visit">
                  <INP value={form.reasonForVisit} onChange={(e:any)=>f('reasonForVisit',e.target.value)} placeholder="Chief complaint or reason for appointment"/>
                </FLD>
              </div>
              <div className="col-span-2">
                <FLD label="Chief Complaint">
                  <INP value={form.chiefComplaint} onChange={(e:any)=>f('chiefComplaint',e.target.value)} placeholder="Primary complaint in patient's words"/>
                </FLD>
              </div>
              <div className="col-span-2">
                <FLD label="Additional Notes">
                  <textarea value={form.notes} onChange={e=>f('notes',e.target.value)} rows={2}
                    placeholder="Any additional information for the doctor…"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#015CA6] outline-none"/>
                </FLD>
              </div>
              <FLD label="Referral Source">
                <SEL value={form.referralSource} onChange={(e:any)=>f('referralSource',e.target.value)}>
                  {['SELF','DOCTOR_REFERRAL','CAMP','ONLINE','INSURANCE','CORPORATE','OTHER'].map(s=><option key={s}>{s}</option>)}
                </SEL>
              </FLD>
              <FLD label="Referred By">
                <INP value={form.referredBy} onChange={(e:any)=>f('referredBy',e.target.value)} placeholder="Dr. Name / Hospital"/>
              </FLD>
              <div className="col-span-2">
                <FLD label="Special Instructions">
                  <INP value={form.specialInstructions} onChange={(e:any)=>f('specialInstructions',e.target.value)} placeholder="e.g. Bring previous reports, fasting required…"/>
                </FLD>
              </div>
              <div className="col-span-2 flex flex-wrap gap-5 pt-1">
                {[['isFollowUp','Follow-up Visit'],['fastingRequired','Fasting Required'],['reminderSms','SMS Reminder'],['reminderWhatsapp','WhatsApp Reminder']].map(([k,l])=>(
                  <label key={k} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={(form as any)[k]} onChange={e=>f(k,e.target.checked)} className="w-4 h-4 rounded accent-[#02357A]"/>
                    <span className="text-sm font-medium text-gray-700">{l}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Payment */}
          <div className="bg-white rounded-xl shadow-sm border p-5">
            <h3 className="font-bold text-gray-800 mb-4 pb-2 border-b">Consultation Fee</h3>
            <div className="grid grid-cols-2 gap-4">
              <FLD label="Payment Mode">
                <SEL value={form.paymentMode} onChange={(e:any)=>f('paymentMode',e.target.value)}>
                  {['CASH','M-PESA','CARD','NHIF','SHA/SHIF','INSURANCE','CORPORATE','WAIVER','FREE'].map(m=><option key={m}>{m}</option>)}
                </SEL>
              </FLD>
              <FLD label="Consultation Fee ({getCurrencySymbol()})">
                <INP type="number" value={form.consultationFee} onChange={(e:any)=>f('consultationFee',e.target.value)} placeholder="500"/>
              </FLD>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end pb-6">
            <button onClick={()=>nav('/appointments')} className="px-6 py-2.5 border rounded-xl hover:bg-gray-50 text-gray-600">{t('common.cancel','Cancel')}</button>
            <button onClick={submit} disabled={saving||!form.patientId||!form.doctorId||!form.appointmentTime}
              className="px-8 py-2.5 bg-[#02357A] text-white rounded-xl hover:bg-[#015CA6] font-semibold disabled:opacity-50 min-w-44">
              {saving?'Booking…':'✓ Book Appointment'}
            </button>
          </div>
          {!form.appointmentTime&&form.doctorId&&form.appointmentDate&&(
            <p className="text-xs text-red-500 text-center -mt-4">⚠️ Please select a time slot to proceed</p>
          )}
        </div>
      </div>
    </Layout>
  );
}
